import { masters } from "@nexseed/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ImagePlus, Trash2 } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/features/auth/store";
import { api, ApiError } from "@/lib/api-client";
import { shrinkPhoto } from "@/lib/photo";

interface Photo {
  id: number;
  fileName: string;
  url: string;
}

/**
 * Photos of a plant or drying facility, under its edit sheet. They hang off a saved row, so a new
 * facility is saved first and gets its photos on the next opening.
 */
export function FacilityPhotos({
  facilityId,
}: {
  facilityId?: number | undefined;
}) {
  return (
    <section className="mt-4">
      <h3 className="mb-2 text-sm font-medium text-ink">Photos (optional)</h3>
      {facilityId === undefined ? (
        <p className="text-sm text-ink-muted">
          Save the facility first, then add photos.
        </p>
      ) : (
        <PhotoGrid facilityId={facilityId} />
      )}
    </section>
  );
}

function PhotoGrid({ facilityId }: { facilityId: number }) {
  const base = `/masters/facilities/${facilityId}/photos`;
  const canEdit = useAuth((s) => s.can)("masters.facilities.edit");
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const photos = useQuery({
    queryKey: ["masters", "facilities", facilityId, "photos"],
    queryFn: () => api<Photo[]>(base),
  });
  const refresh = () =>
    qc.invalidateQueries({
      queryKey: ["masters", "facilities", facilityId, "photos"],
    });
  const errorText = (e: unknown) =>
    e instanceof ApiError ? e.message : "Could not save the photo";

  // Photos go up one at a time, so "3 of 5" is free and truthful. Byte-level progress is not:
  // api() is fetch-based and fetch cannot report upload progress.
  const [sent, setSent] = useState<{ done: number; total: number } | null>(
    null
  );
  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      setSent({ done: 0, total: files.length });
      for (const [i, file] of files.entries()) {
        setSent({ done: i, total: files.length });
        await api<Photo>(base, {
          method: "POST",
          json: {
            fileName: file.name.replace(/\.[^.]*$/, "") + ".jpg",
            mimeType: "image/jpeg",
            base64: await shrinkPhoto(file),
          },
        });
      }
    },
    onSettled: () => {
      setSent(null);
      void refresh();
    },
    onError: (e) => toast.error(errorText(e)),
  });
  const remove = useMutation({
    mutationFn: (id: number) =>
      api<void>(`${base}/${id}`, { method: "DELETE" }),
    onSettled: () => void refresh(),
    onError: (e) => toast.error(errorText(e)),
  });

  const list = photos.data ?? [];
  const room = masters.FACILITY_PHOTO_LIMIT - list.length;
  return (
    <>
      {list.length === 0 && !photos.isLoading && (
        <p className="mb-2 text-sm text-ink-muted">No photos yet.</p>
      )}
      <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {list.map((p) => (
          <li key={p.id} className="relative">
            <a href={p.url} target="_blank" rel="noreferrer">
              <img
                src={p.url}
                alt={p.fileName}
                loading="lazy"
                className="h-28 w-full rounded border border-border object-cover"
              />
            </a>
            {canEdit && (
              <Button
                type="button"
                variant="secondary"
                size="icon-sm"
                className="absolute top-1 right-1"
                aria-label={`Delete ${p.fileName}`}
                disabled={remove.isPending}
                onClick={() => remove.mutate(p.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </li>
        ))}
      </ul>
      {canEdit && (
        <div className="mt-2 flex items-center gap-3">
          <input
            ref={input}
            type="file"
            accept="image/jpeg,image/png"
            multiple
            hidden
            onChange={(e) => {
              const files = [...(e.target.files ?? [])];
              e.target.value = "";
              if (files.length > room)
                toast.error(
                  `A facility holds at most ${masters.FACILITY_PHOTO_LIMIT} photos; ${room} more can be added.`
                );
              else if (files.length) upload.mutate(files);
            }}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            loading={upload.isPending}
            disabled={room <= 0}
            onClick={() => input.current?.click()}
          >
            <ImagePlus className="h-4 w-4" />
            Add photos
          </Button>
          {sent && sent.total > 1 ? (
            <div className="min-w-40 flex-1">
              <Progress
                value={sent.done / sent.total}
                label="Uploading photos"
              />
              <p className="mt-1 text-xs text-ink-muted">
                Uploading {sent.done + 1} of {sent.total}
              </p>
            </div>
          ) : (
            <span className="text-xs text-ink-muted">
              JPEG or PNG · up to {masters.FACILITY_PHOTO_LIMIT}
            </span>
          )}
        </div>
      )}
    </>
  );
}
